import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getChartThemeColors } from '../../utils/chartTheme';
import useUserStore from '../../stores/useUserStore';
import { hasSegment, lineShadow } from './isolatedPoints';

interface DataPoint {
  x: number | string | Date;
  y: number | null;
}

interface ApexSeries {
  name: string;
  data: DataPoint[];
  // Otras props que uses
}

interface LineChartProps {
  series: ApexSeries[];
  xaxisCategories: string[];
  height?: number;
  yaxisTitle: string;
  xaxisTitle?: string;
  selectedCurrency: string;
  tooltipCustom?: (obj: {
    seriesIndex: number;
    dataPointIndex: number;
    w: any;
  }) => string;
  priceView: 'kg' | 'box';
  viewMode?: 'day' | 'week';
  minYAxisValue?: number;
  showExporterPrices?: boolean;
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  /** Optional override for x-axis category labels. When provided it takes
   *  precedence over the default date formatting (used by the retail chart
   *  to render week numbers). */
  xLabelFormatter?: (value: string) => string;
  /** Optional override for y-axis tick labels (used by the retail chart to
   *  render a non-USD local currency). */
  yLabelFormatter?: (value: number) => string;
  /** Optional fixed series palette. When omitted, ApexCharts defaults apply
   *  (keeps existing charts untouched). */
  colors?: string[];
  /** Hide the native legend (e.g. when the caller renders its own richer
   *  legend, like the retail variation card). Defaults to visible. */
  showLegend?: boolean;
  /** Tope de etiquetas del eje X, para ejes largos en gráficos angostos donde
   *  dibujarlas todas las deja ilegibles. Sin definir, se muestran todas. */
  maxXLabels?: number;
  /** Patrón de guiones del trazo, por serie (ej. `[5, 0]` = primera punteada,
   *  segunda continua). Sin definir, todas las líneas van continuas. */
  strokeDashArray?: number | number[];
}

const abbreviateNumber = (value: number): string => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return `${sign}${Math.round(abs)}`;
};

const LineChart: React.FC<LineChartProps> = ({
  series,
  xaxisCategories,
  height = 350,
  yaxisTitle,
  xaxisTitle,
  selectedCurrency,
  tooltipCustom,
  priceView = 'kg',
  viewMode = 'day',
  minYAxisValue = 0,
  legendPosition = 'bottom',
  xLabelFormatter,
  yLabelFormatter,
  colors,
  showLegend = true,
  strokeDashArray,
  maxXLabels,
}) => {
  const { darkMode } = useUserStore();
  const chartColors = getChartThemeColors(darkMode);
  // 1) Limpieza: convierte en null los puntos vacíos
  const cleanedSeries = series.map((serie) => {
    const dataWithNulls = serie.data.map((p) => {
      if (p.y === 0 || p.y === undefined) {
        return { ...p, y: null };
      }
      return p;
    });
    return { ...serie, data: dataWithNulls };
  });

  // 2) Sin dos puntos seguidos no hay línea que dibujar: va como 'scatter'
  //    (ver isolatedPoints.ts).
  const finalSeries = cleanedSeries.map((serie) => {
    const validPoints = serie.data;

    if (!hasSegment(validPoints)) {
      return {
        ...serie,
        type: 'scatter' as const,
        data: validPoints.map((point) => ({
          ...point,
          x: point.x,
          y: point.y,
        })),
        markers: {
          size: 8,
        },
      };
    } else {
      return {
        ...serie,
        type: 'line' as const,
        data: serie.data.map((point) => ({
          ...point,
          x: point.x,
          y: point.y,
        })),
      };
    }
  });

  // Config genérica
  const options: ApexOptions = {
    chart: {
      height,
      type: 'line',
      toolbar: { show: false },
      foreColor: chartColors.foreColor,
      dropShadow: {
        ...lineShadow(finalSeries),
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 0.2,
      },
      zoom: {
        enabled: false,
      },
    },

    ...(colors && { colors }),
    stroke: {
      width: 3,
      curve: 'smooth',
      lineCap: 'butt',
      ...(strokeDashArray !== undefined && { dashArray: strokeDashArray }),
    },
    markers: {
      size: 5,
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: chartColors.gridBorderColor,
      row: {
        colors: chartColors.gridRowColors,
        opacity: 0.5,
      },
    },
    xaxis: {
      type: 'category',
      categories: xaxisCategories,
      // The axis-attached date box duplicates the tooltip header and overlaps
      // the category labels, so it stays off.
      tooltip: { enabled: false },
      ...(xaxisTitle && {
        title: {
          text: xaxisTitle,
        },
      }),
      ...(maxXLabels && xaxisCategories.length > maxXLabels
        ? { tickAmount: maxXLabels }
        : {}),
      labels: {
        rotate: -45,
        rotateAlways: true,
        formatter: function (value) {
          // Caller-provided label override (e.g. retail week numbers)
          if (xLabelFormatter && typeof value === 'string') {
            return xLabelFormatter(value);
          }
          // Format the date string from categories
          if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // Parse YYYY-MM-DD as local date to avoid timezone issues
            const [year, month, day] = value.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);
            return format(localDate, 'dd MMM', { locale: es });
          }
          return value;
        },
      },
    },
    yaxis: {
      min: minYAxisValue,
      title: {
        text: yaxisTitle,
      },
      labels: {
        formatter: function (value) {
          if (yLabelFormatter) return yLabelFormatter(value);
          const symbol = selectedCurrency === 'RMB' ? '¥' : '$';
          return `${symbol}${abbreviateNumber(value)}`;
        },
      },
    },
    tooltip: {
      x: {
        formatter: function (value) {
          // Handle null/undefined
          if (!value) return '';

          // Caller-provided label override (e.g. retail week numbers)
          if (xLabelFormatter && typeof value === 'string') {
            return xLabelFormatter(value);
          }

          // Parse YYYY-MM-DD as local date to avoid timezone issues
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = value.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);
            return format(localDate, 'dd MMM yyyy', { locale: es });
          }

          // Check if value is a reasonable timestamp (e.g. > 1000000) to avoid treating indices as dates
          if (typeof value === 'number') {
            if (value > 1000000) {
              return format(new Date(value), 'dd MMM yyyy', { locale: es });
            }
            // If it's a small number, it might be an index for xaxisCategories
            const index = Math.round(value);
            // ApexCharts category axis usually passes 1-based index, so we subtract 1
            const categoryIndex = index - 1;
            if (categoryIndex >= 0 && xaxisCategories[categoryIndex]) {
              const cat = xaxisCategories[categoryIndex];
              if (typeof cat === 'string' && cat.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = cat.split('-').map(Number);
                const localDate = new Date(year, month - 1, day);
                return format(localDate, 'dd MMM yyyy', { locale: es });
              }
              return String(cat);
            }
          }

          // Fallback - return the value as is if we can't parse it
          return String(value);
        },
      },
      y: {
        formatter: function (value, { seriesIndex, dataPointIndex, w }) {
          if (value === undefined || value === null) return 'N/A';
          const boxFormat = w?.globals?.seriesNames?.[seriesIndex] || '';
          return `${boxFormat}: ${Number(value).toFixed(
            2
          )} ${selectedCurrency}/${priceView}`;
        },
      },
      custom: tooltipCustom,
    },
    legend: {
      show: showLegend,
      showForSingleSeries: true,
      position: legendPosition,
      horizontalAlign: legendPosition === 'right' || legendPosition === 'left' ? 'left' : 'center',
    },
  };

  return (
    <ReactApexChart
      options={options}
      series={finalSeries}
      type='line'
      height={height}
    />
  );
};

export default LineChart;
